import axiosInstance from '../axiosInstance';

/**
 * 결제 생성 (결제 요청 준비)
 * 고객 정보(email/name)는 백엔드가 인증된 유저에서 직접 조회하므로 여기서 전송하지 않는다.
 * @param {number|number[]} bookingIds - 예약 ID 또는 예약 ID 배열
 */
export const createPayment = async (bookingIds) => {
  try {
    const ids = Array.isArray(bookingIds) ? bookingIds : [bookingIds];
    const response = await axiosInstance.post('/payments', {
      bookingIds: ids,
      // 하위 호환: 단일 예약인 경우 bookingId도 함께 전송
      bookingId: ids.length === 1 ? ids[0] : ids[0],
    });
    return response.data.data;
  } catch (error) {
    console.error('Create Payment API Error:', error);
    throw error;
  }
};

/**
 * 결제 조회
 * @param {number} paymentId - 결제 ID
 */
export const getPayment = async (paymentId) => {
  try {
    const response = await axiosInstance.get(`/payments/${paymentId}`);
    return response.data.data;
  } catch (error) {
    console.error('Get Payment API Error:', error);
    throw error;
  }
};

/**
 * 예약 ID로 결제 조회
 * @param {number} bookingId - 예약 ID
 */
export const getPaymentByBookingId = async (bookingId) => {
  try {
    const response = await axiosInstance.get(`/payments/booking/${bookingId}`);
    return response.data.data;
  } catch (error) {
    console.error('Get Payment by Booking ID API Error:', error);
    throw error;
  }
};

/**
 * 주문 ID로 결제 조회
 * @param {string} orderId - 주문 ID
 */
export const getPaymentByOrderId = async (orderId) => {
  try {
    const response = await axiosInstance.get(`/payments/order/${orderId}`);
    return response.data.data;
  } catch (error) {
    console.error('Get Payment by Order ID API Error:', error);
    throw error;
  }
};

/**
 * 정책 동의 기록 (DL-010)
 * 결제 요청 직전 약관/개인정보/환불 정책 동의를 서버에 기록한다.
 * 이 동의가 없으면 백엔드에서 결제 승인이 차단된다.
 * @param {string} orderId - 주문 ID
 */
export const recordPolicyConsent = async (orderId) => {
  try {
    const response = await axiosInstance.post(`/payments/${orderId}/consent`);
    return response.data.data;
  } catch (error) {
    console.error('Record Policy Consent API Error:', error);
    throw error;
  }
};

export const prepareEximbayPayment = async (orderId) => {
  const response = await axiosInstance.post(`/payments/${orderId}/prepare`);
  return response.data.data;
};

/* ── 계좌이체 워크숍 (Prana Club 한옥 웰니스 등) ──────────────────── */

/**
 * 계좌이체 결제 생성. 결제 레코드를 만들고 유저 입금 안내·주최측 알림 메일이 나간다.
 * 응답은 입금 계좌·금액·이체명·마감 시각 (BankTransferInstructionResponse).
 * @param {number[]} bookingIds
 */
export const createBankTransferPayment = async (bookingIds) => {
  const ids = Array.isArray(bookingIds) ? bookingIds : [bookingIds];
  const response = await axiosInstance.post('/payments/bank-transfer', {
    bookingIds: ids,
    bookingId: ids[0],
  });
  return response.data.data;
};

/**
 * 계좌이체 안내 재조회 (안내 페이지 새로고침·마이페이지 재진입)
 * @param {string} orderId
 */
export const getBankTransferInstruction = async (orderId) => {
  const response = await axiosInstance.get(`/payments/bank-transfer/${orderId}`);
  return response.data.data;
};

/**
 * Admin - 계좌이체 입금 확인(확약). 결제 DONE + 예약 CONFIRMED + 확정 메일.
 * 3시간이 지나 만료된 예약도 좌석이 남아 있으면 확약된다.
 * @param {{ invoiceNumber?: string, paymentId?: number, note?: string }} params
 */
export const confirmBankTransfer = async ({ invoiceNumber, paymentId, note }) => {
  const response = await axiosInstance.post('/payments/admin/bank-transfer/confirm', {
    invoiceNumber,
    paymentId,
    note,
  });
  return response.data;
};

/**
 * 내 결제 내역 조회 (마이페이지 결제 목록용)
 * @returns {Promise<Array>} 결제 내역 리스트
 */
export const getMyPaymentHistory = async (
  page = 0,
  size = 20,
  filter = 'ALL'
) => {
  try {
    const params = { page, size };
    if (filter && filter !== 'ALL') {
      params.filter = filter;
    } else {
      params.filter = 'ALL';
    }

    const response = await axiosInstance.get('/payments/my', { params });
    return response.data.data ?? response.data;
  } catch (error) {
    console.error('Get My Payment History API Error:', error);
    throw error;
  }
};

/**

 * Invoice 기준 결제/예약 상세 조회

 * @param {string} invoiceNumber - 인보이스 번호

 */

export const getInvoiceDetail = async (invoiceNumber) => {
  try {
    const response = await axiosInstance.get(
      `/payments/invoice/${invoiceNumber}`
    );

    return response.data.data;
  } catch (error) {
    console.error('Get Invoice Detail API Error:', error);

    throw error;
  }
};

/**
 * Admin - Pending 상태 결제 목록 조회
 */
export const getPendingPayments = async (page = 0, size = 5) => {
  try {
    const response = await axiosInstance.get('/payments/admin/pending', {
      params: { page, size },
    });
    return response.data.data;
  } catch (error) {
    console.error('Get Pending Payments API Error:', error);
    throw error;
  }
};

/**
 * Admin - 결제 요청 리마인더 메일 발송
 */
export const resendPaymentRequest = async (bookingIds) => {
  try {
    const response = await axiosInstance.post(
      '/payments/admin/resend-payment-request',
      {
        bookingIds,
      }
    );
    return response.data.data;
  } catch (error) {
    console.error('Resend Payment Request API Error:', error);
    throw error;
  }
};

/**
 * Admin - Pending 결제 취소
 */
export const cancelPayment = async (bookingIds) => {
  try {
    await axiosInstance.post('/payments/admin/cancel', { bookingIds });
  } catch (error) {
    console.error('Cancel Payment API Error:', error);
    throw error;
  }
};

/**
 * Admin - 결제 환불 처리 (DL-006)
 * @param {Object} params
 * @param {number} params.paymentId
 * @param {number} [params.refundAmount] - 미지정 시 전액 환불
 * @param {string} [params.refundReason]
 * @param {string} [params.refundExceptionReason] - DUPLICATE_PAYMENT/TECHNICAL_ERROR/COMPANY_FAULT/MISREPRESENTATION/STATUTORY/PG_REVERSAL
 * @param {string} [params.adminOverrideReason]
 */
export const refundPayment = async ({
  paymentId,
  refundAmount,
  refundReason,
  refundExceptionReason,
  adminOverrideReason,
}) => {
  try {
    await axiosInstance.post('/payments/admin/refund', {
      paymentId,
      refundAmount,
      refundReason,
      refundExceptionReason,
      adminOverrideReason,
    });
  } catch (error) {
    console.error('Refund Payment API Error:', error);
    throw error;
  }
};

/**
 * Admin - 결제 목록 전체 조회
 */
export const getAllPayments = async (page = 0, size = 12) => {
  try {
    const response = await axiosInstance.get('/payments/admin/all-payments', {
      params: { page, size },
    });

    return response.data.data;
  } catch (error) {
    console.error('Get All Payments API Error:', error);

    throw error;
  }
};

/**
 * Admin - Refunds & Cancellations 목록 조회
 * @param {'ALL'|'REQUESTED'|'COMPLETED'|'REJECTED'|'CANCELED'} filter
 */
export const getCanceledPayments = async (page = 0, size = 20, filter = 'ALL') => {
  try {
    const response = await axiosInstance.get('/payments/admin/canceled', {
      params: { page, size, filter },
    });
    return response.data.data;
  } catch (error) {
    console.error('Get Canceled Payments API Error:', error);
    throw error;
  }
};

/**
 * Admin - 환불 요청 거절. 예약은 CONFIRMED 로 복구되고 유저에게 안내 메일이 간다.
 * @param {{ paymentId: number, reason?: string }} params
 */
export const rejectRefund = async ({ paymentId, reason }) => {
  const response = await axiosInstance.post('/payments/admin/refund/reject', {
    paymentId,
    reason,
  });
  return response.data;
};

/**
 * 결제 히스토리 조회 (paymentId 기준)
 * @param {number} paymentId - 결제 ID
 */
export const getPaymentLogHistories = async (paymentId) => {
  try {
    const response = await axiosInstance.get(
      `/payments/histories/${paymentId}`
    );
    return response.data;
  } catch (error) {
    console.error('Get Payment Log Histories API Error:', error);
    throw error;
  }
};

/**
 * 결제 히스토리 조회 (invoiceNumber 기준 - HOLD/OFFERED 상태용)
 * @param {string} invoiceNumber - 인보이스 번호
 */
export const getPaymentLogHistoriesByInvoice = async (invoiceNumber) => {
  try {
    const response = await axiosInstance.get(
      `/payments/histories/invoice/${invoiceNumber}`
    );
    return response.data;
  } catch (error) {
    console.error('Get Payment Log Histories By Invoice API Error:', error);
    throw error;
  }
};
